// Tracks the pointer across the page and updates the --mx / --my CSS
// custom properties on whichever .rod-card the pointer is currently over.
// `.rod-card::after` reads those vars to draw a radial gradient "spotlight"
// that follows the cursor.
//
// Performance notes:
//   - Single document-level pointermove listener, rAF-throttled, passive.
//   - getBoundingClientRect() is the expensive call. We cache the rect of
//     the currently-hovered card and only re-read it when (a) the pointer
//     enters a different card, or (b) the layout shifts via scroll/resize.
//     This drops `getBoundingClientRect` from "every pointer frame" to
//     "every hover transition", which is what Lighthouse was flagging as
//     forced reflow.
//   - Skipped entirely on touch + reduced-motion devices.

export function useSpotlight() {
  if (import.meta.server) return

  onMounted(() => {
    if (typeof matchMedia !== 'function') return
    if (matchMedia('(hover: none)').matches) return
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let raf:        number | null      = null
    let lastEvent:  PointerEvent | null = null
    let cachedCard: HTMLElement | null = null
    let cachedRect: DOMRect | null     = null

    const invalidate = () => {
      // Layout may have shifted under the cursor; force a fresh rect read
      // on the next pointer frame.
      cachedRect = null
    }

    const tick = () => {
      raf = null
      const ev = lastEvent
      if (!ev) return

      const target = (ev.target as Element | null)?.closest?.('.rod-card') as HTMLElement | null

      if (target) {
        // Re-read the rect only when the card changes or after invalidate().
        if (target !== cachedCard || cachedRect === null) {
          cachedCard = target
          cachedRect = target.getBoundingClientRect()
        }
        // Set both vars in one go — these are CSS custom-property writes
        // on an element with `pointer-events: none` ::after, so they
        // trigger paint but not layout.
        target.style.setProperty('--mx', `${ev.clientX - cachedRect.left}px`)
        target.style.setProperty('--my', `${ev.clientY - cachedRect.top}px`)
      } else if (cachedCard) {
        // Pointer left the previous card — let the overlay fade out.
        cachedCard.style.removeProperty('--mx')
        cachedCard.style.removeProperty('--my')
        cachedCard = null
        cachedRect = null
      }
    }

    const onMove = (e: PointerEvent) => {
      lastEvent = e
      if (raf == null) raf = requestAnimationFrame(tick)
    }

    document.addEventListener('pointermove', onMove, { passive: true })
    window.addEventListener('scroll',  invalidate, { passive: true })
    window.addEventListener('resize',  invalidate, { passive: true })

    onBeforeUnmount(() => {
      document.removeEventListener('pointermove', onMove)
      window.removeEventListener('scroll', invalidate)
      window.removeEventListener('resize', invalidate)
      if (raf != null) cancelAnimationFrame(raf)
    })
  })
}
